/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Audio Uploader & Whisper Configuration Zone (Task 10.5.6)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useRef } from 'react';
import {
  UploadCloud,
  FileAudio,
  Sparkles,
  Coins,
  CheckCircle2,
  Cpu,
  Globe2,
  RefreshCw,
  Loader2,
} from 'lucide-react';
import { useTranslation } from '../../i18n';
import {
  useSubtitleStudioStore,
  WhisperModel,
  SubtitleLanguage,
  formatTimecode,
} from '../../stores/useSubtitleStudioStore';

export const AudioUploaderZone: React.FC = () => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    audioFile,
    model,
    language,
    task,
    wordTimestamps,
    jobStatus,
    setAudioFile,
    loadSampleAudio,
    setModel,
    setLanguage,
    setTask,
    setWordTimestamps,
    startTranscription,
    calculateCreditCost,
  } = useSubtitleStudioStore();

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Simulate reading duration from audio element
    const objectUrl = URL.createObjectURL(file);
    const audio = new Audio(objectUrl);
    audio.onloadedmetadata = () => {
      const duration = Math.max(1, Math.round(audio.duration || 30));
      setAudioFile({
        name: file.name,
        size: file.size,
        type: file.type || 'audio/mpeg',
        url: objectUrl,
        durationSec: duration,
      });
    };
    audio.onerror = () => {
      // Fallback if metadata cannot be decoded
      setAudioFile({
        name: file.name,
        size: file.size,
        type: file.type || 'audio/mpeg',
        url: objectUrl,
        durationSec: 45,
      });
    };
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (!file) return;

    const objectUrl = URL.createObjectURL(file);
    const audio = new Audio(objectUrl);
    audio.onloadedmetadata = () => {
      const duration = Math.max(1, Math.round(audio.duration || 30));
      setAudioFile({
        name: file.name,
        size: file.size,
        type: file.type || 'audio/mpeg',
        url: objectUrl,
        durationSec: duration,
      });
    };
  };

  const isWorking = jobStatus === 'uploading' || jobStatus === 'transcribing';
  const creditCost = calculateCreditCost();

  return (
    <div className="bg-surface rounded-2xl border border-border p-6 shadow-sm">
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Left: Drag Drop / Active File Card */}
        <div className="flex-1">
          <input
            ref={fileInputRef}
            type="file"
            accept="audio/*,video/*,.mp3,.wav,.m4a,.mp4,.webm,.flac,.mkv"
            className="hidden"
            onChange={handleFileSelect}
          />

          {audioFile ? (
            <div className="border border-border rounded-xl p-5 bg-surface-subtle flex flex-col justify-between h-full">
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="p-2 rounded-lg bg-primary/10 text-primary">
                      <FileAudio className="size-5" />
                    </span>
                    <div>
                      <h4 className="text-sm font-semibold text-text truncate max-w-[260px] md:max-w-[400px]">
                        {audioFile.name}
                      </h4>
                      <p className="text-xs text-text-muted">
                        {(audioFile.size / (1024 * 1024)).toFixed(2)} MB • {audioFile.type}
                      </p>
                    </div>
                  </div>
                  <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                    <CheckCircle2 className="size-3.5" />
                    {formatTimecode(audioFile.durationSec)}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-3 mt-4 pt-3 border-t border-border">
                <button
                  type="button"
                  disabled={isWorking}
                  onClick={() => fileInputRef.current?.click()}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg border border-border hover:bg-surface text-text transition-colors"
                >
                  {t('subtitles.changeFileBtn')}
                </button>
                <button
                  type="button"
                  disabled={isWorking}
                  onClick={loadSampleAudio}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg text-primary hover:bg-primary/10 transition-colors flex items-center gap-1.5"
                >
                  <RefreshCw className="size-3.5" />
                  {t('subtitles.loadSampleBtn')}
                </button>
              </div>
            </div>
          ) : (
            <div
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-border hover:border-primary/50 hover:bg-surface-subtle/50 rounded-xl p-8 flex flex-col items-center justify-center text-center cursor-pointer transition-colors h-full min-h-[160px]"
            >
              <div className="p-3 rounded-full bg-primary/10 text-primary mb-3">
                <UploadCloud className="size-6" />
              </div>
              <h4 className="text-sm font-semibold text-text mb-1">
                {t('subtitles.uploadTitle')}
              </h4>
              <p className="text-xs text-text-muted mb-4 max-w-sm">
                {t('subtitles.uploadDesc')}
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="px-4 py-2 text-xs font-medium rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                >
                  {t('subtitles.selectFileBtn')}
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    loadSampleAudio();
                  }}
                  className="px-4 py-2 text-xs font-medium rounded-lg border border-border hover:bg-surface-subtle text-text transition-colors"
                >
                  {t('subtitles.loadSampleBtn')}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Right: Whisper Model & Language Settings */}
        <div className="lg:w-[420px] flex flex-col justify-between border-t lg:border-t-0 lg:border-l border-border pt-4 lg:pt-0 lg:pl-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* Model Selector */}
            <div>
              <label className="text-xs font-semibold text-text flex items-center gap-1 mb-1.5">
                <Cpu className="size-3.5 text-primary" />
                {t('subtitles.modelLabel')}
              </label>
              <select
                disabled={isWorking}
                value={model}
                onChange={(e) => setModel(e.target.value as WhisperModel)}
                className="w-full text-xs rounded-lg border border-border bg-surface px-3 py-2 text-text focus:outline-none focus:ring-2 focus:ring-primary/20"
              >
                <option value="whisper-large-v3">{t('subtitles.modelLarge')}</option>
                <option value="whisper-medium">{t('subtitles.modelMedium')}</option>
                <option value="whisper-small">{t('subtitles.modelSmall')}</option>
              </select>
            </div>

            {/* Language Selector */}
            <div>
              <label className="text-xs font-semibold text-text flex items-center gap-1 mb-1.5">
                <Globe2 className="size-3.5 text-primary" />
                {t('subtitles.languageLabel')}
              </label>
              <select
                disabled={isWorking}
                value={language}
                onChange={(e) => setLanguage(e.target.value as SubtitleLanguage)}
                className="w-full text-xs rounded-lg border border-border bg-surface px-3 py-2 text-text focus:outline-none focus:ring-2 focus:ring-primary/20"
              >
                <option value="auto">{t('subtitles.langAuto')}</option>
                <option value="vi">{t('subtitles.langVi')}</option>
                <option value="en">{t('subtitles.langEn')}</option>
                <option value="ja">{t('subtitles.langJa')}</option>
                <option value="zh">{t('subtitles.langZh')}</option>
                <option value="fr">{t('subtitles.langFr')}</option>
                <option value="de">{t('subtitles.langDe')}</option>
                <option value="es">{t('subtitles.langEs')}</option>
                <option value="ko">{t('subtitles.langKo')}</option>
              </select>
            </div>
          </div>

          {/* Options Toggles */}
          <div className="space-y-2 pt-1 border-t border-border">
            <label className="flex items-center justify-between cursor-pointer group">
              <span className="text-xs text-text font-medium">
                {t('subtitles.translateLabel')}
              </span>
              <input
                type="checkbox"
                disabled={isWorking}
                checked={task === 'translate'}
                onChange={(e) => setTask(e.target.checked ? 'translate' : 'transcribe')}
                className="rounded border-border text-primary focus:ring-primary"
              />
            </label>

            <label className="flex items-center justify-between cursor-pointer group">
              <span className="text-xs text-text font-medium">
                {t('subtitles.wordTimestampsLabel')}
              </span>
              <input
                type="checkbox"
                disabled={isWorking}
                checked={wordTimestamps}
                onChange={(e) => setWordTimestamps(e.target.checked)}
                className="rounded border-border text-primary focus:ring-primary"
              />
            </label>
          </div>

          {/* Action Row & Cost Preview */}
          <div className="pt-2 flex items-center justify-between gap-3">
            <div className="flex items-center gap-1.5 text-xs text-text-muted">
              <Coins className="size-4 text-amber-500" />
              <span>
                {t('subtitles.costEstimateValue', {
                  credits: String(creditCost),
                  seconds: String(Math.round(audioFile?.durationSec || 30)),
                })}
              </span>
            </div>

            <button
              type="button"
              disabled={!audioFile || isWorking}
              onClick={startTranscription}
              className="px-4 py-2 text-xs font-semibold rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-all shadow-sm flex items-center gap-2"
            >
              {isWorking ? (
                <>
                  <Loader2 className="size-4 animate-spin" />
                  <span>
                    {jobStatus === 'uploading'
                      ? t('subtitles.uploadingStatus')
                      : t('subtitles.transcribingStatus')}
                  </span>
                </>
              ) : (
                <>
                  <Sparkles className="size-4" />
                  <span>{t('subtitles.startTranscribeBtn')}</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
